import { parseProblem } from '@reos/api-client';
import { baseUrl } from './api.js';
import { DomainApiError } from './api-error.js';

/**
 * Krok A4: klient nad `/v1/duediligence/correspondence/*` - fronta „dotazy ke
 * schvaleni" a rozhodnuti cloveka o odeslani.
 *
 * Rucni typy ze stejneho duvodu jako v `reports-release-api.ts` (A5): tvar
 * odpovida `utilityMessageDtoSchema` v `api-schemas.ts` a je tu jen podmnozina,
 * kterou obrazovka opravdu cte.
 */

export interface PendingCorrespondenceItem {
  readonly id: string;
  readonly caseId: string | null;
  readonly recipientKind: string;
  readonly recipientName: string | null;
  readonly messageKind: string;
  readonly toAddr: string | null;
  readonly subject: string | null;
  readonly bodyText: string | null;
  readonly questionKeys: readonly string[];
  readonly dueAt: string | null;
  readonly attachmentCount: number;
  readonly modelGenerated: boolean;
  readonly createdAt: string;
}

async function unwrapFetch<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const problem = await parseProblem(res).catch(() => null);
    throw new DomainApiError(
      problem?.detail ?? problem?.title ?? `HTTP ${res.status}`,
      res.status,
      problem?.type,
    );
  }
  return (await res.json()) as T;
}

export async function listPendingCorrespondence(): Promise<readonly PendingCorrespondenceItem[]> {
  const res = await fetch(`${baseUrl}/v1/duediligence/correspondence/pending`, {
    credentials: 'include',
    headers: { accept: 'application/json' },
  });
  const body = await unwrapFetch<{ items: PendingCorrespondenceItem[] }>(res);
  return body.items;
}

/**
 * Schvaleni odeslani. `recipientAddress` je POVINNA jen u navrhu, ktery adresu
 * nema (SVJ, obec) - system ji nehada, doplni ji clovek.
 */
export async function approveCorrespondence(
  messageId: string,
  input: { readonly recipientAddress?: string | undefined; readonly note?: string | undefined },
): Promise<void> {
  const body: Record<string, string> = {};
  const address = input.recipientAddress?.trim() ?? '';
  const note = input.note?.trim() ?? '';
  if (address !== '') body['recipientAddress'] = address;
  if (note !== '') body['note'] = note;
  const res = await fetch(
    `${baseUrl}/v1/duediligence/correspondence/${encodeURIComponent(messageId)}/approve`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  await unwrapFetch<unknown>(res);
}

/** Zamitnuti navrhu - duvod je POVINNY (vynucuje i API). */
export async function rejectCorrespondence(messageId: string, reason: string): Promise<void> {
  const res = await fetch(
    `${baseUrl}/v1/duediligence/correspondence/${encodeURIComponent(messageId)}/reject`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ reason }),
    },
  );
  await unwrapFetch<unknown>(res);
}
